import React, { useEffect, useState } from 'react';
import config from '../../config';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #f5f6fa;
    --surface: #ffffff;
    --surface2: #f9fafb;
    --border: #e5e7eb;
    --accent: #e85d04;
    --accent-light: #fff4ee;
    --accent-border: #fdd0b1;
    --green: #059669;
    --green-bg: #ecfdf5;
    --green-border: #a7f3d0;
    --amber: #92400e;
    --amber-bg: #fffbeb;
    --amber-border: #fde68a;
    --indigo: #3730a3;
    --indigo-bg: #eef2ff;
    --indigo-border: #c7d2fe;
    --red-bg: #fef2f2;
    --red-border: #fecaca;
    --red-text: #b91c1c;
    --text: #111827;
    --text2: #374151;
    --muted: #6b7280;
    --muted2: #9ca3af;
    --radius: 12px;
    --radius-sm: 8px;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
    --shadow: 0 2px 8px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04);
  }

  .pk-root {
    font-family: 'Inter', sans-serif;
    background: var(--bg);
    min-height: 100vh;
    color: var(--text);
    padding: 28px 24px 48px;
  }

  /* ── PAGE HEADER ── */
  .pk-page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
    gap: 16px;
    flex-wrap: wrap;
  }
  .pk-page-header-left {}
  .pk-breadcrumb {
    font-size: 12px;
    color: var(--muted2);
    font-weight: 500;
    display: flex; align-items: center; gap: 6px;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .pk-breadcrumb span { color: var(--accent); }
  .pk-page-title {
    font-size: 22px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -0.3px;
  }
  .pk-live-pill {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--green);
    background: var(--green-bg);
    border: 1px solid var(--green-border);
    padding: 6px 14px;
    border-radius: 20px;
    flex-shrink: 0;
    align-self: flex-start;
    margin-top: 4px;
  }
  .pk-live-dot {
    width: 7px; height: 7px;
    background: var(--green);
    border-radius: 50%;
    animation: blink 1.5s ease-in-out infinite;
    flex-shrink: 0;
  }
  @keyframes blink {
    0%,100% { opacity: 1; }
    50% { opacity: 0.3; }
  }

  /* ── STAT CARDS ── */
  .pk-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 20px;
  }
  .pk-stat-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 16px 18px;
    box-shadow: var(--shadow-sm);
    display: flex; align-items: center; gap: 14px;
  }
  .pk-stat-icon {
    width: 42px; height: 42px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }
  .pk-stat-icon.orange { background: var(--accent-light); }
  .pk-stat-icon.indigo { background: var(--indigo-bg); }
  .pk-stat-num {
    font-size: 24px;
    font-weight: 700;
    color: var(--text);
    line-height: 1;
  }
  .pk-stat-label {
    font-size: 12px;
    color: var(--muted);
    font-weight: 500;
    margin-top: 3px;
  }

  /* ── TABS ── */
  .pk-tabs {
    display: flex;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 4px;
    gap: 4px;
    margin-bottom: 20px;
    box-shadow: var(--shadow-sm);
  }
  .pk-tab {
    flex: 1;
    padding: 10px 12px;
    border: none; outline: none;
    background: transparent;
    color: var(--muted);
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.18s ease;
    display: flex; align-items: center; justify-content: center; gap: 7px;
  }
  .pk-tab:hover { background: var(--surface2); color: var(--text2); }
  .pk-tab.active {
    background: #2563eb;
    color: #fff;
    box-shadow: 0 2px 8px rgba(37,99,235,0.30);
  }
  .pk-tab-badge {
    font-size: 11px;
    font-weight: 700;
    padding: 1px 7px;
    border-radius: 10px;
    background: rgba(255,255,255,0.25);
    color: #fff;
  }
  .pk-tab:not(.active) .pk-tab-badge {
    background: var(--surface2);
    color: var(--muted);
    border: 1px solid var(--border);
  }

  /* ── ALERT ── */
  .pk-alert {
    background: var(--red-bg);
    border: 1px solid var(--red-border);
    color: var(--red-text);
    padding: 12px 16px;
    border-radius: var(--radius-sm);
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 16px;
    display: flex; align-items: center; gap: 8px;
  }

  /* ── LOADING ── */
  .pk-loading {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 72px 20px; gap: 14px;
  }
  .pk-spinner {
    width: 36px; height: 36px;
    border: 3px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .pk-loading-text { color: var(--muted); font-size: 13px; font-weight: 500; }

  /* ── EMPTY ── */
  .pk-empty {
    display: flex; flex-direction: column; align-items: center;
    padding: 56px 20px; gap: 10px;
    background: var(--surface);
    border-radius: var(--radius);
    border: 1px dashed var(--border);
  }
  .pk-empty-icon { font-size: 40px; opacity: 0.35; }
  .pk-empty-text { font-size: 14px; font-weight: 600; color: var(--muted); }
  .pk-empty-sub { font-size: 12px; color: var(--muted2); }

  /* ── ITEM WISE ── */
  .pk-item-list { display: flex; flex-direction: column; gap: 8px; }

  .pk-item-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    box-shadow: var(--shadow-sm);
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .pk-item-card:hover { border-color: var(--accent-border); }
  .pk-item-card.expanded { border-color: var(--accent); }

  .pk-item-header {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 18px;
    cursor: pointer;
    user-select: none;
    background: var(--surface);
  }
  .pk-item-card.expanded .pk-item-header {
    background: var(--accent-light);
    border-bottom: 1px solid var(--accent-border);
  }

  .pk-item-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--accent-border);
    flex-shrink: 0;
    transition: background 0.15s;
  }
  .pk-item-card.expanded .pk-item-dot { background: var(--accent); }

  .pk-item-name {
    font-size: 14px;
    font-weight: 600;
    flex: 1;
    color: var(--text);
  }

  .pk-item-qty-badge {
    background: var(--amber-bg);
    color: var(--amber);
    border: 1px solid var(--amber-border);
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .pk-item-qty-badge::before { content: '×'; margin-right: 2px; opacity: 0.6; }

  .pk-item-tables-hint {
    font-size: 12px;
    color: var(--muted2);
    font-weight: 500;
    margin-right: 4px;
  }
  .pk-item-card.expanded .pk-item-tables-hint { color: var(--accent); }

  .pk-chevron {
    color: var(--muted2);
    font-size: 16px;
    transition: transform 0.22s, color 0.15s;
    flex-shrink: 0;
  }
  .pk-item-card.expanded .pk-chevron { transform: rotate(180deg); color: var(--accent); }

  /* Accordion body */
  .pk-item-body {
    display: none;
    animation: fadeSlide 0.18s ease;
  }
  .pk-item-body.open { display: block; }
  @keyframes fadeSlide { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }

  .pk-table-row {
    display: flex; align-items: center;
    padding: 12px 18px;
    border-bottom: 1px solid var(--border);
    gap: 12px;
    background: var(--surface2);
    transition: background 0.12s;
  }
  .pk-table-row:last-child { border-bottom: none; }
  .pk-table-row:hover { background: #f3f4f6; }

  .pk-table-no {
    background: var(--indigo-bg);
    color: var(--indigo);
    border: 1px solid var(--indigo-border);
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    min-width: 56px;
    text-align: center;
    flex-shrink: 0;
  }
  .pk-table-info { flex: 1; }
  .pk-table-qty-text { font-size: 13px; font-weight: 600; color: var(--text2); }
  .pk-table-qty-text b { color: var(--text); font-weight: 700; }

  .pk-delivered-btn {
    background: var(--green);
    color: #fff;
    border: none; outline: none;
    padding: 7px 16px;
    border-radius: 7px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: flex; align-items: center; gap: 5px;
    transition: all 0.15s ease;
    flex-shrink: 0;
    box-shadow: 0 1px 4px rgba(5,150,105,0.2);
  }
  .pk-delivered-btn:hover {
    background: #047857;
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(5,150,105,0.3);
  }
  .pk-delivered-btn:active { transform: none; box-shadow: none; }

  /* ── TABLE WISE ── */
  .pk-twise-list { display: flex; flex-direction: column; gap: 8px; }

  .pk-twise-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px 18px;
    display: flex; align-items: center; gap: 14px;
    box-shadow: var(--shadow-sm);
    transition: border-color 0.15s, transform 0.15s, box-shadow 0.15s;
    flex-wrap: wrap;
  }
  .pk-twise-card:hover {
    border-color: var(--accent-border);
    box-shadow: var(--shadow);
  }

  .pk-tno-badge {
    background: var(--indigo-bg);
    color: var(--indigo);
    border: 1px solid var(--indigo-border);
    padding: 8px 14px;
    border-radius: 9px;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
    min-width: 60px;
    text-align: center;
  }
  .pk-tno-label {
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: var(--indigo);
    opacity: 0.6;
    display: block;
    margin-bottom: 1px;
  }

  .pk-twise-info { flex: 1; min-width: 0; }
  .pk-twise-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .pk-twise-cat {
    font-size: 11px;
    color: var(--muted);
    font-weight: 500;
    margin-top: 3px;
    display: flex; align-items: center; gap: 4px;
  }
  .pk-twise-cat::before { content: ''; width: 4px; height: 4px; background: var(--muted2); border-radius: 50%; flex-shrink: 0; }

  .pk-qty-chip {
    background: var(--amber-bg);
    color: var(--amber);
    border: 1px solid var(--amber-border);
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }

  /* ── SNACKBAR ── */
  .pk-snack {
    position: fixed;
    bottom: 24px; left: 50%;
    transform: translateX(-50%) translateY(90px);
    background: #1f2937;
    color: #fff;
    padding: 12px 20px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    display: flex; align-items: center; gap: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
    transition: transform 0.32s cubic-bezier(.34,1.56,.64,1);
    z-index: 9999;
    white-space: nowrap;
    border-left: 3px solid var(--green);
  }
  .pk-snack.show { transform: translateX(-50%) translateY(0); }

  /* ── RESPONSIVE ── */
  @media (min-width: 640px) {
    .pk-twise-card { flex-wrap: nowrap; }
  }
  @media (min-width: 768px) {
    .pk-root { padding: 32px 32px 56px; max-width: 860px; margin: 0 auto; }
  }
  @media (min-width: 1024px) {
    .pk-root { padding: 36px 40px 64px; max-width: 960px; }
  }

  /* ── KDS KOT cards (Table Wise) ── */
  .pk-kot-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
  }
  .pk-kot-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .pk-kot-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: var(--accent-light);
    border-bottom: 1px solid var(--accent-border);
  }
  .pk-kot-no {
    font-weight: 700;
    font-size: 15px;
    color: var(--accent);
    letter-spacing: 0.2px;
  }
  .pk-kot-loc { font-weight: 600; font-size: 14px; color: var(--text2); }
  .pk-kot-items {
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }
  .pk-kot-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 8px;
    border-radius: var(--radius-sm);
    transition: background 0.12s;
  }
  .pk-kot-item:hover { background: var(--surface2); }
  .pk-kot-qty {
    font-weight: 800;
    font-size: 15px;
    color: var(--accent);
    min-width: 34px;
  }
  .pk-kot-iname {
    flex: 1;
    font-size: 15px;
    font-weight: 500;
    color: var(--text);
    line-height: 1.3;
  }
  .pk-kot-deliver {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid var(--green-border);
    background: var(--green-bg);
    color: var(--green);
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.14s;
  }
  .pk-kot-deliver:hover { background: var(--green); color: #fff; }
  .pk-kot-cats {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0 16px 14px;
  }
  .pk-kot-cat {
    font-size: 11px;
    font-weight: 600;
    color: var(--indigo);
    background: var(--indigo-bg);
    border: 1px solid var(--indigo-border);
    border-radius: 6px;
    padding: 3px 8px;
  }
`;

export default function PendingKitchen() {
  const [tabValue, setTabValue] = useState(0);
  const [itemWiseData, setItemWiseData] = useState([]);
  const [tableWiseData, setTableWiseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [openAccordions, setOpenAccordions] = useState({});

  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, [tabValue]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const url =
        tabValue === 0
          ? `${config.API_BASE_URL}/home/PendingKitchenItem/${company.id}`
          : `${config.API_BASE_URL}/home/GetPendingOrder/${company.id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (tabValue === 0) setItemWiseData(Array.isArray(data) ? data : []);
      else setTableWiseData(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelivered = async (orderId, tableNo, itemName) => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/DeliveredKitchenItem`,
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },

          body: JSON.stringify({
            companyId: company.id,
            // OrderId is the precise key (room orders have no table). tableNo is
            // kept as a fallback for older table-only rows.
            orderId: orderId || 0,
            tableNo: tableNo,
            itemName: itemName,
          }),
        },
      );

      if (!res.ok) throw new Error('Failed');

      setSuccessMsg('Item marked as delivered');

      setTimeout(() => setSuccessMsg(''), 2500);

      fetchData();
    } catch {
      setError('Failed to deliver item');
    }
  };

  const toggleAccordion = (idx) =>
    setOpenAccordions((prev) => ({ ...prev, [idx]: !prev[idx] }));

  const totalQty = itemWiseData.reduce((s, i) => s + (i.totalQty || 0), 0);

  // KDS: group the flat table-wise item lines into one card per KOT (order).
  // Pure client-side reshaping of the SAME data - no new API, no new fields.
  const kotGroups = React.useMemo(() => {
    const map = new Map();
    tableWiseData.forEach((row) => {
      const key = row.orderid;
      if (!map.has(key)) {
        map.set(key, { orderid: row.orderid, tableno: row.tableno, items: [] });
      }
      map.get(key).items.push({
        name: row.name,
        qty: row.qty,
        category: row.category_name,
      });
    });
    return Array.from(map.values());
  }, [tableWiseData]);

  return (
    <>
      <style>{styles}</style>
      <div className='pk-root'>
        {/* PAGE HEADER */}
        <div className='pk-page-header'>
          <div className='pk-page-header-left'>
            <div className='pk-breadcrumb'>
              Restaurant &nbsp;/&nbsp; <span>Kitchen Orders</span>
            </div>
            <div className='pk-page-title'>Pending Kitchen Orders</div>
          </div>
          <div className='pk-live-pill'>
            <div className='pk-live-dot' />
            Live
          </div>
        </div>

        {/* STAT CARDS */}
        <div className='pk-stats'>
          <div className='pk-stat-card'>
            <div className='pk-stat-icon orange'>🍽️</div>
            <div>
              <div className='pk-stat-num'>{itemWiseData.length}</div>
              <div className='pk-stat-label'>Item Types</div>
            </div>
          </div>
          <div className='pk-stat-card'>
            <div className='pk-stat-icon indigo'>🪑</div>
            <div>
              <div className='pk-stat-num'>{tableWiseData.length}</div>
              <div className='pk-stat-label'>Table Orders</div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className='pk-tabs'>
          <button
            className={`pk-tab ${tabValue === 0 ? 'active' : ''}`}
            onClick={() => setTabValue(0)}>
            🍽️ Item Wise
            {itemWiseData.length > 0 && (
              <span className='pk-tab-badge'>{itemWiseData.length}</span>
            )}
          </button>
          <button
            className={`pk-tab ${tabValue === 1 ? 'active' : ''}`}
            onClick={() => setTabValue(1)}>
            🪑 Table Wise
            {tableWiseData.length > 0 && (
              <span className='pk-tab-badge'>{tableWiseData.length}</span>
            )}
          </button>
        </div>

        {/* ERROR */}
        {error && <div className='pk-alert'>⚠️ {error}</div>}

        {/* LOADING */}
        {loading ? (
          <div className='pk-loading'>
            <div className='pk-spinner' />
            <div className='pk-loading-text'>Loading orders...</div>
          </div>
        ) : (
          <>
            {/* ITEM WISE */}
            {tabValue === 0 && (
              <div className='pk-item-list'>
                {itemWiseData.length > 0 ? (
                  itemWiseData.map((item, index) => (
                    <div
                      className={`pk-item-card ${openAccordions[index] ? 'expanded' : ''}`}
                      key={index}>
                      <div
                        className='pk-item-header'
                        onClick={() => toggleAccordion(index)}>
                        <div className='pk-item-dot' />
                        <span
                          style={{
                            display: 'inline-flex',
                            width: 12,
                            height: 12,
                            marginRight: 6,
                            border: `2px solid ${
                              item.foodType === 'NonVeg'
                                ? '#c0392b'
                                : item.foodType === 'Egg'
                                  ? '#e67e22'
                                  : '#0a8a3c'
                            }`,
                            borderRadius: 2,
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background:
                                item.foodType === 'NonVeg'
                                  ? '#c0392b'
                                  : item.foodType === 'Egg'
                                    ? '#e67e22'
                                    : '#0a8a3c',
                            }}
                          />
                        </span>
                        <div className='pk-item-name'>{item.itemName}</div>
                        <div className='pk-item-qty-badge'>{item.totalQty}</div>
                        <span className='pk-item-tables-hint'>
                          {item.tableDetails?.length || 0} table
                          {item.tableDetails?.length !== 1 ? 's' : ''}
                        </span>
                        <span className='pk-chevron'>▾</span>
                      </div>
                      <div
                        className={`pk-item-body ${openAccordions[index] ? 'open' : ''}`}>
                        {item.tableDetails?.map((table, idx) => (
                          <div className='pk-table-row' key={idx}>
                            <div className='pk-table-no'>
                              T — {table.tableNo}
                            </div>
                            <div className='pk-table-info'>
                              <div className='pk-table-qty-text'>
                                Qty: <b>{table.qty}</b>
                              </div>
                            </div>
                            <button
                              className='pk-delivered-btn'
                              onClick={() =>
                                handleDelivered(
                                  table.orderId,
                                  table.tableNo,
                                  item.itemName,
                                )
                              }>
                              ✓ Delivered
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='pk-empty'>
                    <div className='pk-empty-icon'>🍽️</div>
                    <div className='pk-empty-text'>No pending items</div>
                    <div className='pk-empty-sub'>
                      All kitchen items have been delivered
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TABLE WISE — KDS KOT cards */}
            {tabValue === 1 && (
              <div className='pk-kot-grid'>
                {kotGroups.length > 0 ? (
                  kotGroups.map((kot) => {
                    const isRoom = /room/i.test(kot.tableno || '');
                    const cats = [
                      ...new Set(
                        kot.items.map((i) => i.category).filter(Boolean),
                      ),
                    ];
                    return (
                      <div className='pk-kot-card' key={kot.orderid}>
                        <div className='pk-kot-head'>
                          <span className='pk-kot-no'>KOT #{kot.orderid}</span>
                          <span className='pk-kot-loc'>
                            {isRoom ? '🛎️' : '🍽️'} {kot.tableno}
                          </span>
                        </div>
                        <div className='pk-kot-items'>
                          {kot.items.map((it, i) => (
                            <div className='pk-kot-item' key={i}>
                              <span className='pk-kot-qty'>{it.qty}×</span>
                              <span className='pk-kot-iname'>{it.name}</span>
                              <button
                                className='pk-kot-deliver'
                                title='Mark delivered'
                                onClick={() =>
                                  handleDelivered(
                                    kot.orderid,
                                    kot.tableno,
                                    it.name,
                                  )
                                }>
                                ✓
                              </button>
                            </div>
                          ))}
                        </div>
                        {cats.length > 0 && (
                          <div className='pk-kot-cats'>
                            {cats.map((c, i) => (
                              <span className='pk-kot-cat' key={i}>
                                {c}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className='pk-empty'>
                    <div className='pk-empty-icon'>🪑</div>
                    <div className='pk-empty-text'>No pending orders</div>
                    <div className='pk-empty-sub'>
                      All table orders have been delivered
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* SNACKBAR */}
        <div className={`pk-snack ${successMsg ? 'show' : ''}`}>
          ✓ {successMsg}
        </div>
      </div>
    </>
  );
}
